import { z } from 'zod';
import { FEEDBACK_BREADCRUMB_RESULTS, FEEDBACK_BREADCRUMB_TYPES, FEEDBACK_DIAGNOSTIC_SECTIONS, FEEDBACK_MAX_BREADCRUMBS, FEEDBACK_PRIORITIES, FEEDBACK_REFERENCE_PATTERN, FEEDBACK_REPRODUCIBILITY, FEEDBACK_STATUSES, FEEDBACK_TYPES, FEEDBACK_URGENCIES, } from '../constants/feedback.js';
import { errorTrackingIdSchema, systemErrorLogSchema } from './system-error-log.js';
import { idSchema, isoDateTimeSchema } from './common.js';
import { paginationMetaSchema, paginationQuerySchema } from './pagination.js';
export const feedbackTypeSchema = z.enum(FEEDBACK_TYPES);
export const feedbackUrgencySchema = z.enum(FEEDBACK_URGENCIES);
export const feedbackReproducibilitySchema = z.enum(FEEDBACK_REPRODUCIBILITY);
export const feedbackStatusSchema = z.enum(FEEDBACK_STATUSES);
export const feedbackPrioritySchema = z.enum(FEEDBACK_PRIORITIES);
export const feedbackReferenceSchema = z.string().regex(FEEDBACK_REFERENCE_PATTERN);
export const feedbackDiagnosticSectionSchema = z.enum(FEEDBACK_DIAGNOSTIC_SECTIONS);
const stableIdentifierSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9_.:/-]*$/u);
export const feedbackBreadcrumbSchema = z
    .object({
    timestamp: isoDateTimeSchema,
    type: z.enum(FEEDBACK_BREADCRUMB_TYPES),
    route: z.string().max(500),
    feature: stableIdentifierSchema,
    actionId: stableIdentifierSchema,
    actionLabelKey: stableIdentifierSchema.nullable(),
    entityType: stableIdentifierSchema.nullable(),
    entityId: z.string().trim().max(100).nullable(),
    result: z.enum(FEEDBACK_BREADCRUMB_RESULTS),
    errorTrackingId: errorTrackingIdSchema.nullable(),
})
    .strict();
const feedbackApplicationDiagnosticsSchema = z
    .object({
    capturedAtUtc: isoDateTimeSchema,
    localTime: z.string().max(80),
    localTimezone: z.string().max(100),
    appVersion: z.string().max(100),
    buildCommit: z.string().max(100).nullable(),
    apiVersion: z.string().max(30),
    deploymentEnvironment: z.string().max(50),
    frontendBuildTime: z.string().max(100).nullable(),
    featureFlags: z.array(stableIdentifierSchema).max(200),
})
    .strict();
const sanitizedQueryParameterSchema = z
    .object({
    key: stableIdentifierSchema,
    value: z.string().max(100),
})
    .strict();
const feedbackPageDiagnosticsSchema = z
    .object({
    pathname: z.string().max(500),
    queryParameters: z.array(sanitizedQueryParameterSchema).max(20),
    routePattern: z.string().max(500),
    pageTitle: z.string().max(200),
    helpArticleId: stableIdentifierSchema.nullable(),
    activeTab: stableIdentifierSchema.nullable(),
    businessArea: stableIdentifierSchema,
    entityType: stableIdentifierSchema.nullable(),
    entityId: z.string().trim().max(100).nullable(),
    internalReferrer: z.string().max(500).nullable(),
})
    .strict();
const feedbackBrowserDiagnosticsSchema = z
    .object({
    userAgent: z.string().max(1000),
    browserName: z.string().max(80),
    browserVersion: z.string().max(80),
    operatingSystem: z.string().max(100),
    deviceType: z.enum(['DESKTOP', 'TABLET', 'MOBILE', 'UNKNOWN']),
    viewportWidth: z.number().int().min(0).max(100_000),
    viewportHeight: z.number().int().min(0).max(100_000),
    screenWidth: z.number().int().min(0).max(100_000),
    screenHeight: z.number().int().min(0).max(100_000),
    devicePixelRatio: z.number().min(0).max(20),
    touchSupported: z.boolean(),
    preferredColorScheme: z.enum(['LIGHT', 'DARK']),
    erpTheme: z.enum(['light', 'dark', 'system']),
    browserLanguage: z.string().max(50),
    erpLanguage: z.string().max(50),
    timezone: z.string().max(100),
    online: z.boolean(),
    connectionType: z.string().max(50).nullable(),
    standalone: z.boolean(),
    deviceMemoryGb: z.number().min(0).max(1024).nullable(),
})
    .strict();
export const feedbackDiagnosticsSchema = z
    .object({
    includedSections: z.array(feedbackDiagnosticSectionSchema).max(4),
    application: feedbackApplicationDiagnosticsSchema.nullable(),
    page: feedbackPageDiagnosticsSchema.nullable(),
    browser: feedbackBrowserDiagnosticsSchema.nullable(),
    breadcrumbs: z.array(feedbackBreadcrumbSchema).max(FEEDBACK_MAX_BREADCRUMBS),
})
    .strict();
const optionalFeedbackText = (max) => z.string().trim().max(max).nullish();
export const feedbackCreatePayloadSchema = z
    .object({
    type: feedbackTypeSchema,
    title: z.string().trim().min(5).max(160),
    description: z.string().trim().min(20).max(10_000),
    attemptedAction: optionalFeedbackText(4000),
    actualResult: optionalFeedbackText(4000),
    expectedResult: optionalFeedbackText(4000),
    reproducibility: feedbackReproducibilitySchema.nullish(),
    reproductionSteps: optionalFeedbackText(6000),
    urgency: feedbackUrgencySchema.default('NORMAL'),
    affectedFeature: optionalFeedbackText(160),
    wantsFollowUp: z.boolean().default(false),
    includeExtendedDiagnostics: z.boolean().default(true),
    problematicFieldIds: z.array(stableIdentifierSchema).max(50).default([]),
    diagnostics: feedbackDiagnosticsSchema,
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.type !== 'BUG')
        return;
    for (const field of ['attemptedAction', 'actualResult', 'expectedResult']) {
        if ((value[field] ?? '').trim().length < 5) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: 'Dieses Feld ist für Fehlermeldungen erforderlich.',
            });
        }
    }
    if (value.reproducibility === null || value.reproducibility === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reproducibility'],
            message: 'Bitte geben Sie an, ob das Problem wiederholbar ist.',
        });
    }
});
export const feedbackCreateResponseSchema = z
    .object({
    id: idSchema,
    reference: feedbackReferenceSchema,
    status: feedbackStatusSchema,
    createdAt: isoDateTimeSchema,
})
    .strict();
export const feedbackReportSchema = z
    .object({
    id: idSchema,
    reference: feedbackReferenceSchema,
    type: feedbackTypeSchema,
    title: z.string(),
    description: z.string(),
    urgency: feedbackUrgencySchema,
    affectedFeature: z.string().nullable(),
    wantsFollowUp: z.boolean(),
    status: feedbackStatusSchema,
    adminPriority: feedbackPrioritySchema.nullable(),
    createdByUserId: idSchema,
    createdByName: z.string(),
    userRole: z.string(),
    assignedToUserId: idSchema.nullable(),
    assignedToName: z.string().nullable(),
    hasScreenshot: z.boolean(),
    linkedErrorTrackingIds: z.array(errorTrackingIdSchema),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    resolvedAt: isoDateTimeSchema.nullable(),
})
    .strict();
export const feedbackReportDetailSchema = feedbackReportSchema
    .extend({
    tenantId: idSchema,
    attemptedAction: z.string().nullable(),
    actualResult: z.string().nullable(),
    expectedResult: z.string().nullable(),
    reproducibility: feedbackReproducibilitySchema.nullable(),
    reproductionSteps: z.string().nullable(),
    includeExtendedDiagnostics: z.boolean(),
    problematicFieldIds: z.array(stableIdentifierSchema),
    tenantName: z.string(),
    tenantSlug: z.string(),
    permissionKeys: z.array(z.string()),
    userLocale: z.string(),
    diagnostics: feedbackDiagnosticsSchema,
    adminNotes: z.string().nullable(),
    screenshotAttachmentId: idSchema.nullable(),
    systemErrors: z.array(systemErrorLogSchema),
})
    .strict();
export const feedbackListQuerySchema = paginationQuerySchema.omit({ sort: true }).extend({
    q: z.string().trim().max(200).optional(),
    type: feedbackTypeSchema.optional(),
    status: feedbackStatusSchema.optional(),
    priority: feedbackPrioritySchema.optional(),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
});
export const feedbackListResponseSchema = z
    .object({
    data: z.array(feedbackReportSchema),
    meta: paginationMetaSchema,
    summary: z.object({
        openCount: z.number().int().min(0),
        untriagedCount: z.number().int().min(0),
        last24hCount: z.number().int().min(0),
    }),
})
    .strict();
export const feedbackAdminUpdateSchema = z
    .object({
    status: feedbackStatusSchema.optional(),
    adminPriority: feedbackPrioritySchema.nullish(),
    assignedToUserId: idSchema.nullish(),
    adminNotes: optionalFeedbackText(6000),
    linkedErrorTrackingIds: z.array(errorTrackingIdSchema).max(20).optional(),
})
    .strict()
    .refine((value) => Object.values(value).some((entry) => entry !== undefined), {
    message: 'Mindestens ein Feld muss aktualisiert werden.',
});
export const feedbackSupportPromptSchema = z
    .object({
    reference: feedbackReferenceSchema,
    prompt: z.string().min(1).max(40_000),
})
    .strict();
//# sourceMappingURL=feedback.js.map